import * as Sentry from '@sentry/react-router';
import type { GraphQLError } from 'graphql';
import type { ClientError } from 'graphql-request';
import { isTruthy } from './predicate';

function mapGraphQLError({
  error,
  operationName,
}: {
  error: unknown;
  operationName?: string;
}) {
  const originalError = (error as ClientError)?.response?.errors?.[0]
    ?.extensions?.originalError as GraphQLError;

  const errorMessage = isTruthy(originalError)
    ? originalError?.message
    : ((error as Error)?.message ??
      `Unknown error on operation: ${operationName ?? 'Unknown'}`);

  return errorMessage;
}

function logSentryError({
  name,
  message,
  stack,
  source,
  tags,
  extra,
  level = 'error',
}: LogSentryErrorInterface): void {
  Sentry?.withScope((scope) => {
    const error = new CustomError({
      name,
      message,
      stack,
    });

    scope.setLevel(level);
    scope.setTag('source', source);

    if (tags) {
      tags.forEach(({ name: tagName, value }) => scope.setTag(tagName, value));
    }

    if (extra) {
      scope.setExtra('additionalData', extra);
    }

    Sentry.captureException(error);
  });
}

interface CustomErrorInterface {
  name: Error['name'];
  message: Error['message'];
  stack?: Error['stack'];
}

class CustomError extends Error {
  public constructor({ name, message, stack }: CustomErrorInterface) {
    super(message);
    this.name = name;
    if (stack) {
      this.stack = stack;
    }
  }
}

interface TagInterface {
  name: string;
  value: string;
}

interface LogSentryErrorInterface {
  name: Error['name'];
  message: Error['message'];
  source: 'client' | 'server';
  stack?: Error['stack'];
  tags?: TagInterface[];
  extra?: Record<string, unknown>;
  level?: Sentry.SeverityLevel;
}

export { mapGraphQLError, logSentryError };
